import {
  NotificationTemplateTypes,
  Pagination,
  PermissionEnum,
} from '../../base-types';
import { ColumnProps, FiltersSchema, SchemaProps } from '../table/types';
import { PickerPosition } from '../date-input/types';

export enum LockReasonEnum {
  USER_TOO_MANY_WRONG_PASSWORDS = 'USER_TOO_MANY_WRONG_PASSWORDS',
  USER_TOO_MANY_WRONG_2FA_CODES = 'USER_TOO_MANY_WRONG_2FA_CODES',
  USER_TOO_MANY_WRONG_PINS = 'USER_TOO_MANY_WRONG_PINS',
  USER_TOO_MANY_VERIFY_SMS = 'USER_TOO_MANY_VERIFY_SMS',
  USER_TOO_MANY_VERIFY_EMAILS = 'USER_TOO_MANY_VERIFY_EMAILS',
  USER_TOO_MANY_VERIFY_PHONE_ATTEMPTS = 'USER_TOO_MANY_VERIFY_PHONE_ATTEMPTS',
  GLOBAL_SYSTEM_DOWNTIME = 'GLOBAL_SYSTEM_DOWNTIME',
  TWILIO_THROTTLE = 'TWILIO_THROTTLE',
  MANUAL = 'MANUAL',
}

export enum LockReasonTitle {
  USER_TOO_MANY_WRONG_PASSWORDS = 'User too many wrong passowrds',
  USER_TOO_MANY_WRONG_2FA_CODES = 'User too many wrong 2FA codes',
  USER_TOO_MANY_WRONG_PINS = 'User too many wrong pins',
  USER_TOO_MANY_VERIFY_SMS = 'User too many verify sms',
  USER_TOO_MANY_VERIFY_EMAILS = 'User too many verify emails',
  USER_TOO_MANY_VERIFY_PHONE_ATTEMPTS = 'User too many verify phone attempts',
  GLOBAL_SYSTEM_DOWNTIME = 'Global system downtime',
  TWILIO_THROTTLE = 'Twilio throttle',
  MANUAL = 'Manual',
}

export enum LockAuditLogActionEnum {
  USER_AUTH_EMAIL = 'USER_AUTH_EMAIL',
  USER_AUTH_PHONE = 'USER_AUTH_PHONE',
  USER_VERIFY_TOTP = 'USER_VERIFY_TOTP',
  USER_ACTIVATE_TOTP = 'USER_ACTIVATE_TOTP',
  USER_SEND_VALIDATE_CODE_TOTP = 'USER_SEND_VALIDATE_CODE_TOTP',
  USER_GENERATE_TOTP = 'USER_GENERATE_TOTP',
  USER_DELETE_TOTP = 'USER_DELETE_TOTP',
  USER_VERIFY_CODE = 'USER_VERIFY_CODE',
  USER_VERIFY_PASSWORD = 'USER_VERIFY_PASSWORD',
  USER_AUTH_PIN = 'USER_AUTH_PIN',
  USER_SET_PIN = 'USER_SET_PIN',
  USER_SEND_RESET_PASSWORD = 'USER_SEND_RESET_PASSWORD',
  USER_VERIFY_RESET_CODE = 'USER_VERIFY_RESET_CODE',
  USER_REFRESH_TOKEN = 'USER_REFRESH_TOKEN',
  USER_CONVERT_TOKEN = 'USER_CONVERT_TOKEN',
  USER_SIGNATURE_VERIFY_CODE = 'USER_SIGNATURE_VERIFY_CODE',
  USER_GENERATE_CHECKSUM = 'USER_GENERATE_CHECKSUM',
  USER_VERIFY_SIGNATURE = 'USER_VERIFY_SIGNATURE',
  AUTH_CREDENTIAL_PHONE_SEND_SMS = 'AUTH_CREDENTIAL_PHONE_SEND_SMS',
  USER_SEND_EMAIL_CODE_SIGNATURE = 'USER_SEND_EMAIL_CODE_SIGNATURE',
  USER_SEND_PUSH_CODE_SIGNATURE = 'USER_SEND_PUSH_CODE_SIGNATURE',
  USER_SEND_SMS_CODE_SIGNATURE = 'USER_SEND_SMS_CODE_SIGNATURE',
  USER_SEND_TOTP_CODE_SIGNATURE = 'USER_SEND_TOTP_CODE_SIGNATURE',
  USER_FORBIDDEN = 'USER_FORBIDDEN',
}

export enum LockAuditLogTitle {
  USER_AUTH_EMAIL = 'User auth email',
  USER_AUTH_PHONE = 'User auth phone',
  USER_VERIFY_TOTP = 'User verify totp',
  USER_ACTIVATE_TOTP = 'User activate totp',
  USER_SEND_VALIDATE_CODE_TOTP = 'User send validate code totp',
  USER_GENERATE_TOTP = 'User generate totp',
  USER_DELETE_TOTP = 'User delete totp',
  USER_VERIFY_CODE = 'User verify code',
  USER_VERIFY_PASSWORD = 'User verify password',
  USER_AUTH_PIN = 'User auth pin',
  USER_SET_PIN = 'User set pin',
  USER_SEND_RESET_PASSWORD = 'User send reset password',
  USER_VERIFY_RESET_CODE = 'User verify reset code',
  USER_REFRESH_TOKEN = 'User refresh token',
  USER_CONVERT_TOKEN = 'User convert token',
  USER_SIGNATURE_VERIFY_CODE = 'User signature verify code',
  USER_GENERATE_CHECKSUM = 'User generate checksum',
  USER_VERIFY_SIGNATURE = 'User verify signature',
  AUTH_CREDENTIAL_PHONE_SEND_SMS = 'Auth credential phone send sms',
  USER_SEND_EMAIL_CODE_SIGNATURE = 'User send email code signature',
  USER_SEND_PUSH_CODE_SIGNATURE = 'User send push code signature',
  USER_SEND_SMS_CODE_SIGNATURE = 'User send sms code signature',
  USER_SEND_TOTP_CODE_SIGNATURE = 'User send totp code signature',
  USER_FORBIDDEN = 'User Forbidden',
}

export enum LockAuditLogResultEnum {
  SUCCESSFUL = 'SUCCESSFUL',
  FAILED = 'FAILED',
}

export interface Lock {
  id: string;
  locked: boolean;
  reason: LockReasonEnum;
  userId?: string;
  createdByEmployeeId?: string;
  expiresAt?: string;
  createdByComment?: string;
  createdAt: string;
  type: string;
  render?: Record<string, React.JSX.Element>;
}

export enum UserStatuses {
  ACTIVE = 'ACTIVE',
  DISABLED = 'DISABLED',
}

export interface UserDetails {
  id: string;
  employeeRoleTemplateId: string;
  profileId: string;
  profile: {
    id: string;
    userId: string;
    firstName: string;
    lastName: string;
    avatarImg: string;
  };
  employeePermissions: {
    permissions: PermissionEnum[];
  }[];
  status: UserStatuses;
  email: string;
  emailPrivate: string;
  ipsWhitelist: string[];
  employeeRoleTemplateName?: string;
  canSignTransactions: boolean;
  notificationsTypes: NotificationTemplateTypes[];
  notificationTypeTemplateId?: string;
  isCustomNotificationsTypes?: boolean;
}

export interface LockAuditLog {
  id: string;
  action: LockAuditLogActionEnum;
  ipSource?: string;
  userId?: string;
  result: LockAuditLogResultEnum;
  lockId?: string;
  deviceId?: string;
  audience?: string;
  nextAction?: string;
  createdAt?: string;
  eventCreatedAt?: string;
  render?: Record<string, React.JSX.Element>;
}

export interface LockFormValues {
  reason: string;
  userId?: string;
  locked?: boolean;
  expiresAt?: string;
  createdByComment?: string;
}

export interface LocksListTypes {
  locks?: Lock[];
  pagination?: Pagination;
  takeCount?: number;
  users?: UserDetails[];
  schema: SchemaProps;
  getList: any;
  filtersSchema: FiltersSchema[];
  isFetching: boolean;
  isLoading: boolean;
  columns: ColumnProps[];
  columnsLabels: Record<string, unknown>;
  userId?: string;
  tableTopClassName?: string;
  expirationDatesPickerPosition?: PickerPosition;
  withPickerTitles?: boolean;
}

export interface LogsListTypes {
  logs?: LockAuditLog[];
  pagination?: Pagination;
  takeCount?: number;
  activePermissions?: PermissionEnum[];
  schema: SchemaProps;
  getList: any;
  filtersSchema: FiltersSchema[];
  userId?: string;
  isLoading: boolean;
  columns: ColumnProps[];
  columnsLabels: Record<string, unknown>;
  isFetching: boolean;
  tableTopClassName?: string;
}

export interface LockFormTypes {
  getLockItem: any;
  getLocks: any;
  create: any;
  update: any;
  getOrganizations?: any;
  getLockItemUser?: any;
  getUsers?: any;
  lockItem?: Lock;
  takeCount?: number;
  lockItemUser?: {
    id: string;
    profile: {
      firstName: string;
      lastName: string;
    };
  };
  activePermissions?: PermissionEnum[];
  employees?: UserDetails[];
  clearLockPanelState: () => void;
  onClose: () => void;
  userId?: string;
  isSuccess?: boolean;
  organizationsIsLoading?: boolean;
  organizationsIsFetching?: boolean;
  usersIsLoading?: boolean;
  usersIsFetching?: boolean;
  isLoading?: boolean;
  isFetching?: boolean;
  requestInProgress?: boolean;
  updateInProgress?: boolean;
  withoutUserSelector?: boolean;
  lockIdValue?: string;
  formPanelVisible?: boolean;
  onBlockUserClick?: () => void;
  lockItemAuditLogs?: LockAuditLog[];
  lockItemAuditLogsPagination?: Pagination;
  getLockItemLogsList: any;
  lockItemLogsSchema: SchemaProps;
  lockItemLogsFiltersSchema: FiltersSchema[];
  lockItemLogsColumns: ColumnProps[];
  lockItemLogsColumnsLabels: Record<string, unknown>;
  lockItemLogsIsLoading?: boolean;
  lockItemLogsIsFetching?: boolean;
}

export interface LockLogsListTypes {
  lockItemAuditLogs?: LockAuditLog[];
  lockItemAuditLogsPagination?: Pagination;
  getList: any;
  schema: SchemaProps;
  filtersSchema: FiltersSchema[];
  activePermissions?: PermissionEnum[];
  columns: ColumnProps[];
  columnsLabels: Record<string, unknown>;
  isLoading?: boolean;
  isFetching?: boolean;
  lockId?: string;
}

export interface LogFormTypes {
  clearLogPanelState: () => void;
  onClose: () => void;
  getLogItem: any;
  getLockItemUser?: any;
  activePermissions?: PermissionEnum[];
  logItem?: LockAuditLog;
  logItemUser?: {
    serviceRole?: string[];
    id: string;
    profile: {
      firstName: string;
      lastName: string;
    };
  };
  isLoading?: boolean;
  isFetching?: boolean;
  wuthoutCustomer?: boolean;
}

export interface SidepanelLocksListTypes {
  users?: UserDetails[];
  locks?: Lock[];
  locksPagination?: Pagination;
  userId?: string;
  getList: any;
  schema: SchemaProps;
  filtersSchema: FiltersSchema[];
  setLockId: (id: string) => void;
  onBlockUserClick: () => void;
  isFetching: boolean;
  activePermissions?: PermissionEnum[];
  panelIsOpen: boolean;
  isLoading?: boolean;
  columns: ColumnProps[];
  columnsLabels: Record<string, unknown>;
  listClassName?: string;
  withoutChangeSearchParams?: boolean;
}

export interface SidepanelLogsListTypes {
  auditLogs?: LockAuditLog[];
  auditLogsPagination?: Pagination;
  panelIsOpen: boolean;
  activePermissions?: PermissionEnum[];
  userId?: string;
  getList: any;
  schema: SchemaProps;
  filtersSchema: FiltersSchema[];
  isFetching: boolean;
  isLoading?: boolean;
  columns: ColumnProps[];
  columnsLabels: Record<string, unknown>;
  listClassName?: string;
  withoutChangeSearchParams?: boolean;
}
